/**
 * Module dependencies.
 */

import { Disclosure, Type } from 'src/components/core';
import { ReactElement, useMemo, useState } from 'react';
import { switchProp, theme } from 'styled-tools';
import { units } from '@untile/react-components';
import { useAdvisor } from 'src/context/advisor/context';
import SidebarStep from './sidebar-step';
import Transition from 'react-transition-group/Transition';
import map from 'lodash/map';
import styled, { css } from 'styled-components';
import useTranslate from 'src/hooks/use-translate';

/**
 * `Props` type.
 */

type Props = {
  className?: string;
  hasChoices: boolean;
};

/**
 * `Wrapper` styled component.
 */

const Wrapper = styled.div`
  display: flex;
  flex-direction: column;
  margin-top: ${units(6)};
  overflow: auto;
  position: relative;
  width: 100%;
`;

/**
 * `ContentWrapper` styled component.
 */

const ContentWrapper = styled.div`
  display: flex;
  flex-direction: column;
  grid-area: content;
`;

/**
 * `StyledDisclosure` styled component.
 */

const StyledDisclosure = styled(Disclosure)<{ transitionState: any }>`
  transition: opacity ${theme('animations.defaultTransition')};

  ${switchProp('transitionState', {
    entered: css`
      opacity: 1;
    `,
    entering: css`
      opacity: 1;
    `,
    exited: css`
      opacity: 0;
    `,
    exiting: css`
      opacity: 0;
    `
  })}
`;

/**
 * `StepsWrapper` styled component.
 */

const StepsWrapper = styled.div`
  display: grid;
  grid-auto-columns: 100%;
  grid-row-gap: ${units(3)};
  padding-bottom: ${units(3)};
`;

/**
 * `MobileSidebar` component.
 */

const MobileSidebar = ({ className, hasChoices }: Props): ReactElement => {
  const { translate } = useTranslate();
  const { choices, onGoToStep } = useAdvisor();
  const [isOpen, setIsOpen] = useState<boolean>();
  const totalChoices = useMemo(() => choices.length, [choices]);

  return (
    <Wrapper className={className}>
      <ContentWrapper>
        <Type.H2 marginBottom={units(1)}>{translate('advisor:title')}</Type.H2>

        <Type.Paragraph marginBottom={units(5)}>
          {totalChoices > 1
            ? translate('advisor:yourChoices')
            : translate('advisor:lead')}
        </Type.Paragraph>

        <Transition
          in={hasChoices}
          mountOnEnter
          timeout={500}
          unmountOnExit
        >
          {state => (
            <StyledDisclosure
              isAdvisorDisclosure
              isOpen={isOpen}
              reverse
              title={translate(
                `common:actions.${isOpen ? 'close' : 'view'}PreviousChoices`
              )}
              toggle={() => setIsOpen(!isOpen)}
              transitionState={state}
            >
              <StepsWrapper>
                {map(choices, ({ pageKey, value }, index) => (
                  <SidebarStep
                    key={index}
                    onClick={() => onGoToStep(index)}
                    pageKey={pageKey}
                    value={String(value)}
                  />
                ))}
              </StepsWrapper>
            </StyledDisclosure>
          )}
        </Transition>
      </ContentWrapper>
    </Wrapper>
  );
};

/**
 * Export `MobileSidebar` component.
 */

export default MobileSidebar;
